// Client-side image compression + batching for uploads.
// The server (nginx) rejects large request bodies with 413, so images are
// shrunk before upload and files are split into small requests.

const MAX_DIMENSION = 1920;          // longest side in px
const TARGET_FILE_BYTES = 450 * 1024; // aim for each image to be under this
const START_QUALITY = 0.82;
const MIN_QUALITY = 0.5;

// Max body size per upload request. Keep below the server's limit
// (nginx default is 1 MB). Raise this if client_max_body_size is increased.
export const MAX_BATCH_BYTES = 900 * 1024;

// Formats we can safely re-encode (skip GIF to keep animation, SVG is vector).
const COMPRESSIBLE = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];

export async function compressImage(file: File): Promise<File> {
  if (!COMPRESSIBLE.includes(file.type.toLowerCase())) return file;

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    return file; // unreadable by the browser — send as-is
  }

  const alreadySmall =
    file.size <= TARGET_FILE_BYTES &&
    Math.max(img.naturalWidth, img.naturalHeight) <= MAX_DIMENSION;
  if (alreadySmall) return file;

  let scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
  let best: Blob | null = null;

  // Lower quality first, then dimensions, until under the target size.
  for (let attempt = 0; attempt < 4; attempt++) {
    for (let q = START_QUALITY; q >= MIN_QUALITY; q -= 0.1) {
      const blob = await encode(img, scale, q);
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= TARGET_FILE_BYTES) break;
    }
    if (best && best.size <= TARGET_FILE_BYTES) break;
    scale *= 0.8;
  }

  if (!best || best.size >= file.size) return file; // compression didn't help

  const ext = best.type === 'image/webp' ? 'webp' : 'jpg';
  const name = file.name.replace(/\.[^.]+$/, '') + '.' + ext;
  return new File([best], name, { type: best.type, lastModified: Date.now() });
}

// Compress every image in the FormData and split it into batches that stay
// under MAX_BATCH_BYTES. Non-file fields are copied into every batch.
export async function compressAndBatchFormData(
  source: FormData,
  maxBatchBytes = MAX_BATCH_BYTES
): Promise<FormData[]> {
  const fields: [string, string][] = [];
  const files: { key: string; file: File }[] = [];

  source.forEach((value, key) => {
    if (value instanceof File) files.push({ key, file: value });
    else fields.push([key, value]);
  });

  const compressed = await Promise.all(
    files.map(async ({ key, file }) => ({ key, file: await compressImage(file) }))
  );

  const newBatch = () => {
    const fd = new FormData();
    fields.forEach(([k, v]) => fd.append(k, v));
    return fd;
  };

  const batches: FormData[] = [];
  let current = newBatch();
  let currentSize = 0;
  let currentCount = 0;

  for (const { key, file } of compressed) {
    if (currentCount > 0 && currentSize + file.size > maxBatchBytes) {
      batches.push(current);
      current = newBatch();
      currentSize = 0;
      currentCount = 0;
    }
    current.append(key, file, file.name);
    currentSize += file.size;
    currentCount++;
  }
  if (currentCount > 0 || batches.length === 0) batches.push(current);

  return batches;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

// Encode as WebP (keeps transparency). Browsers that can't encode WebP fall
// back to JPEG on a white background so transparent PNGs don't turn black.
async function encode(img: HTMLImageElement, scale: number, quality: number): Promise<Blob> {
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  const webp = await toBlob(canvas, 'image/webp', quality);
  if (webp && webp.type === 'image/webp') return webp;

  ctx.globalCompositeOperation = 'destination-over';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  const jpeg = await toBlob(canvas, 'image/jpeg', quality);
  if (!jpeg) throw new Error('Image encoding failed');
  return jpeg;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}
